import React, { useMemo, useState, useRef, useEffect } from "react"
import { Box, Flex, Heading, Text, Button } from "@chakra-ui/react"

import { Location } from "@root/types/global"
import Breadcrumb from "@components/Breadcrumb"
import Link from "@components/Link"
import Icon from "@components/Icon/Icon"
import { useRoutes } from "@hooks/useRoutes"
import { useContent } from "@hooks/useContent"
import TileCarousel from "../TileCarousel/TileCarousel"
import Image from "@app/components/Image/Image"
import { useMedia } from "@app/hooks/useMedia"

export interface CollectionFeaturedListProps {
  collection: any
  location: Location
}

const CollectionFeaturedList = ({ collection, location }: CollectionFeaturedListProps) => {
  const { urlResolver } = useRoutes()
  const { getContent } = useContent()
  const bottomText = getContent({ content: collection?.bottomText })
  const { isBase } = useMedia()

  const [isExpanded, setIsExpanded] = useState(false)
  const [isTruncated, setIsTruncated] = useState(false)
  const textRef = useRef(null)

  useEffect(() => {
    if (textRef.current) {
      setIsTruncated(textRef.current.scrollHeight > textRef.current.clientHeight)
    }
  }, [])

  const toggleDescription = () => {
    setIsExpanded(!isExpanded)
  }

  const renderBottomText = () => {
    if (!isBase) {
      return <Box minHeight="48px">{bottomText}</Box>
    }

    return (
      <Box 
        minHeight="75px"
      >
        <Box
          ref={textRef}
          overflow="hidden"
          minHeight="48px"
          maxHeight={isExpanded ? "none" : "48px"}
          position="relative"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: isExpanded ? "unset" : 2,
            WebkitBoxOrient: "vertical",
            maxWidth: { base: "100%", sm: "70%" },
            margin: "0 auto",
          }}
        >
          {bottomText}
        </Box>
        {isTruncated && (
          <Button
            onClick={toggleDescription}
            variant="link"
            size="sm"
            color="black"
            fontWeight="normal"
            padding={0}
            height="auto"
            minWidth={0}
            textDecoration="underline"
            _hover={{ textDecoration: "underline" }}
          >
            {isExpanded ? "show less" : "read more"}
          </Button>
        )}
      </Box>
    )
  }

  const items = useMemo(
    () => collection?.featuredCollections?.map(collection => ({ ...collection, url: urlResolver(collection)?.url })),
    [collection?.featuredCollections, urlResolver]
  )

  const settings = {
    slidesPerView: "auto",
    enableScrollbar: false,
    enableNavigation: false,
    spaceBetween: 2
  }

  return (
    <Flex
      px={isBase ? 1.5 : 2}
      maxW="100%"
      overflow="hidden"
      py={isBase ? 1.5 : 2}
      bg="brand.beige.dark"
      flexDirection="column"
      alignItems="center"
    >
      <Flex flexDirection="column">
        <Breadcrumb data={collection} location={location} style={{ textAlign: "center" }} />
        <Heading pt={1} as="h1" size="h1" paddingTop={0} mt="0 !important" textAlign="center">
          {collection.title}
        </Heading>
        <Text 
          size="sm" 
          textAlign="center" 
          mt="0 !important"
          sx={{
            maxWidth: { base: "100%", sm: "70%" },
            margin: "0 auto",
          }}
        >
          {bottomText && renderBottomText()}
        </Text>
      </Flex>
      <Box maxW="100%" mt={isBase ? 2 : 4}>
        <TileCarousel carouselSettings={settings}>
          {items?.map((featuredCollection, idx) => {
            return (
              <Link key={idx} to={featuredCollection.url} color="text.secondary">
                <Box>
                  <Image
                    objectFit="cover"
                    src={featuredCollection.image}
                    borderRadius={`sm`}
                    width={["120px", "205px"]}
                    height={["150px", "250px"]}
                  />
                  <Box pb={1}></Box>
                  <Flex width={{ base: "119px", sm: "200px" }} alignItems="center">
                    {featuredCollection.title}
                    <Icon name="chevronRight" color="text.secondary"></Icon>
                  </Flex>
                </Box>
              </Link>
            )
          })}
        </TileCarousel>
      </Box>
    </Flex>
  )
}

export default CollectionFeaturedList
